import { FileHandler } from "@tiptap/extension-file-handler";
import { Placeholder } from "@tiptap/extension-placeholder";
import { TableKit } from "@tiptap/extension-table";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { Markdown } from "@tiptap/markdown";
import { type Editor, EditorContext, type UseEditorOptions, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { type FC, type PropsWithChildren, useMemo } from "react";
import { EditorExtensionMarkdownPaste } from "./editor-extensions";
import { QW_EDITOR_TABLE_CLASS_NAME, QwEditorTableView } from "./qw-editor-table-view";

const extensions = [
  TextStyleKit,
  StarterKit.configure({
    link: {
      openOnClick: false,
      enableClickSelection: true,
      defaultProtocol: "https",
      protocols: ["ftp", "mailto", "tel"],
    },
  }),
  TableKit.configure({
    table: {
      resizable: true,
      HTMLAttributes: {
        class: QW_EDITOR_TABLE_CLASS_NAME,
      },
      // Custom NodeView so the `qw-editor-table` class lands on the table
      // element in editable mode too — the default TableView ignores
      // HTMLAttributes when building its DOM.
      View: QwEditorTableView,
    },
  }),
  Markdown,
  EditorExtensionMarkdownPaste,
];

export interface EditorProviderProps extends UseEditorOptions {
  placeholder?: string;
  onDropFile?: (editor: Editor, files: File[], pos: number) => void;
  onPasteFile?: (editor: Editor, files: File[], pasteContent?: string) => void;
}

export const EditorProvider: FC<PropsWithChildren<EditorProviderProps>> = ({
  children,
  placeholder,
  onDropFile,
  onPasteFile,
  ...editorOptions
}) => {
  const editor = useEditor({
    ...editorOptions,
    extensions: [
      ...extensions,
      Placeholder.configure({ placeholder }),
      FileHandler.configure({
        onDrop: onDropFile,
        onPaste: onPasteFile,
      }),
      ...(editorOptions.extensions ?? []),
    ],
  });

  const value = useMemo(() => ({ editor }), [editor]);

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};
